import React, { memo } from 'react'
import { Typography } from '@mui/material'
import { ChevronRightMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import type { CollectDataStep } from 'src/interfaces/collectData'
import { RecollectionDeadlineType } from 'src/constants/collect-data/enums'
import {
  getDeactivationBannerVariant,
  getRecollectionDeadlineType,
  getStepDeadlineDays,
  getStepDisplayData,
} from 'src/utils/collectData'
import { RecollectionUnderReviewPill } from './RecollectionUnderReviewPill'

interface Props {
  step: CollectDataStep
  variant: 'actionNeeded' | 'underReview'
  onClick?(): void
}

const deadlineTextStyles = {
  amber: tw`text-warning-500`,
  red: tw`text-critical-500`,
  repeated: tw`text-warning-500`,
  optional: tw`text-shades-400`,
  noDeadline: tw`text-primary-500`,
}

const getDeadlineLabel = (step: CollectDataStep): { text: string; style: any } => {
  const deadlineType = getRecollectionDeadlineType(step)
  const deadlineDays = getStepDeadlineDays(step)

  if (deadlineType === RecollectionDeadlineType.Optional) {
    return { text: 'Optional', style: deadlineTextStyles.optional }
  }

  if (deadlineType === RecollectionDeadlineType.NoDeadline) {
    return { text: 'Update required', style: deadlineTextStyles.noDeadline }
  }

  if (deadlineType === RecollectionDeadlineType.Repeated) {
    if (deadlineDays === null) {
      return { text: 'Update required', style: deadlineTextStyles.noDeadline }
    }

    const repeatedVariant = getDeactivationBannerVariant(deadlineDays)
    const repeatedStyle =
      repeatedVariant === 'amber' ? deadlineTextStyles.amber : deadlineTextStyles.red

    if (deadlineDays > 0) {
      return { text: `${deadlineDays} days remaining`, style: deadlineTextStyles.repeated }
    }

    return { text: 'Expired', style: repeatedStyle }
  }

  if (deadlineDays === null) {
    return { text: '', style: deadlineTextStyles.optional }
  }

  const variant = getDeactivationBannerVariant(deadlineDays)
  const style = variant === 'amber' ? deadlineTextStyles.amber : deadlineTextStyles.red

  if (deadlineDays > 0) {
    return { text: `Expires in ${deadlineDays} days`, style }
  }

  if (deadlineDays === 0) {
    return { text: 'Expires today', style }
  }

  return { text: `Expired · ${dayjs(step.expiresAt).format('MMM D, YYYY')}`, style }
}

const RecollectionStepCardComponent = ({ step, variant, onClick }: Props) => {
  const stepData = getStepDisplayData(step, true)
  const deadline = getDeadlineLabel(step)

  return (
    <div
      css={[
        tw`flex flex-col p-4 bg-shades-0 rounded-2xl border-[1px] border-shades-100 mb-3`,
        variant === 'actionNeeded' && tw`cursor-pointer`,
      ]}
      onClick={variant === 'actionNeeded' ? onClick : undefined}
      role={variant === 'actionNeeded' ? 'button' : undefined}
      tabIndex={variant === 'actionNeeded' ? 0 : undefined}
      onKeyDown={
        variant === 'actionNeeded'
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                onClick?.()
              }
            }
          : undefined
      }
      data-testid={`recollection-step-card-${step.id}`}
    >
      <div css={tw`flex items-center gap-4`}>
        <div
          css={tw`w-11 h-11 flex justify-center items-center rounded-full bg-shades-100 shrink-0`}
        >
          {stepData.icon}
        </div>
        <div css={tw`flex flex-col flex-1`}>
          <Typography variant="h4">{stepData.title}</Typography>
          {deadline.text && (
            <Typography
              variant="caption"
              fontWeight={400}
              css={variant === 'actionNeeded' ? deadline.style : tw`text-shades-400`}
            >
              {deadline.text}
            </Typography>
          )}
        </div>
        {variant === 'actionNeeded' && <ChevronRightMinor width={20} css={tw`fill-[#B4AFC0]`} />}
      </div>
      {variant === 'actionNeeded' ? (
        <Typography css={tw`text-shades-400 mt-3`}>{stepData.text}</Typography>
      ) : (
        <div css={tw`mt-3`}>
          <RecollectionUnderReviewPill />
        </div>
      )}
    </div>
  )
}

export const RecollectionStepCard = memo(RecollectionStepCardComponent)
